// ─── Guided Setup: attendance, set up in full ─────────────────────────────────
//
// Everything ATTENDANCE config holds, so an installer finishes here rather than
// being sent to the Attendance Setup page mid-wizard: the check-in points (with
// the zone each one needs to work at all), the shift hours they run in, when a
// no-show becomes absent, and the employee directory.
//
// The zone is not optional and this panel does not pretend otherwise. A
// checkpoint with no polygon is skipped outright by the engine — attendance.py
// only considers a checkpoint `if cp.get("zones") and _in_window(...)` — so a
// zone-less point is a row in a list that never once fires. It cannot be saved
// from here without one.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import PatrolZoneEditor from '../PatrolZoneEditor';
import { Field, SettingRow, DayPicker, Choice, Advanced, countChanged, useModuleSaver }
  from './SetupBits';

// Site-wide attendance settings. `ask: true` is the one a plant genuinely has to
// answer — when a no-show becomes absent follows the shift, and 11:00 is only
// right for a morning shift. The rest are right nearly everywhere and sit under
// Advanced; `def` matches attendance.default_config() exactly.
const SETTINGS = [
  { key: 'absent_after', label: 'Call a no-show absent after', type: 'time', def: '11:00', ask: true,
    hint: 'Before this time somebody with no check-in is still AWAITED. Without it the whole company reads as absent at 08:00 every morning and the number is noise until lunch.' },
  { key: 'face_min_conf', label: 'Confidence to punch automatically', type: 'range',
    min: 0.1, max: 0.95, step: 0.05, def: 0.5,
    hint: 'Below this the face is treated as unrecognised and sent for review instead, so a weak match can never mark the wrong employee present.' },
  { key: 'dedup_seconds', label: 'Ignore a repeat punch for (s)', type: 'int',
    min: 0, max: 3600, step: 30, def: 120,
    hint: 'Counted per person across ALL cameras — it is what stops one walk-in being punched by the gate camera and again by the lobby camera.' },
  { key: 'retention_days', label: 'Keep records for (days)', type: 'int',
    min: 1, max: 1825, step: 30, def: 90 },
];

// Per-checkpoint settings that have a sensible default. The ones that do NOT —
// name, camera, direction, the zone, the shift window — are asked outright.
const POINT_ADVANCED = [
  { key: 'exception_on_gesture', label: 'Let an unknown face raise a hand', type: 'bool', def: true,
    hint: 'How a visitor, a new joiner or a contractor gets into the list at all: an unrecognised person who raises a hand becomes an EXCEPTION for somebody to credit afterwards, rather than nothing at all.' },
  { key: 'gesture_hold_seconds', label: 'Hand held for (s)', type: 'range',
    min: 0.5, max: 5, step: 0.5, def: 1.5 },
  { key: 'cooldown_seconds', label: 'Ignore the same person for (s)', type: 'int',
    min: 0, max: 3600, step: 60, def: 300,
    hint: 'At this point only — somebody waiting by the door is not punched over and over.' },
  { key: 'min_presence_seconds', label: 'Seconds inside the area', type: 'range',
    min: 0.5, max: 10, step: 0.5, def: 1,
    hint: 'A walk-through, not a queue: the engine samples about once a second, so this is really the tolerance for a roughly drawn area.' },
];

const PURPOSES = [
  { id: 'checkin',  icon: '➡️', title: 'Way in',  sub: 'Marks a clock-in' },
  { id: 'checkout', icon: '⬅️', title: 'Way out', sub: 'Marks a clock-out' },
];

const blankPoint = cam => ({
  name: '', camera_idx: cam, purpose: 'checkin', zones: [],
  schedule: { start: '08:30', end: '19:00', active_days: [0, 1, 2, 3, 4, 5, 6] },
  late_after: '', active: true,
  ...Object.fromEntries(POINT_ADVANCED.map(s => [s.key, s.def])),
});

export default function AttendancePanel({ cameras, onChanged }) {
  const [cfg,   setCfg]   = useState(null);
  const [staff, setStaff] = useState([]);       // enrolled Known Faces, for the directory
  const [edit,  setEdit]  = useState(null);     // the checkpoint being added / changed
  const { busy, err, note, setErr, run } = useModuleSaver(apiFetch, onChanged);

  const load = useCallback(() => apiFetch('/ui/attendance/config')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) setCfg(d); })
    .catch(() => setErr('Could not read the attendance settings.')), [setErr]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/ui/attendance/form-options')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setStaff(Array.isArray(d?.employees) ? d.employees : []))
      .catch(() => {});
  }, []);

  const reload = async () => { await load(); if (onChanged) await onChanged(); };

  const points    = cfg?.checkpoints || [];
  const employees = cfg?.employees || [];
  const camOf     = i => cameras.find(c => c.idx === i)?.name || `Camera ${i}`;

  const saveSetting = (key, val) =>
    run('PUT', '/ui/attendance/settings', { [key]: val }, null).then(d => d && setCfg(d));

  async function savePoint() {
    const body = {
      name: (edit.name || '').trim() || 'Attendance Point',
      camera_idx: edit.camera_idx,
      purpose: edit.purpose,
      zones: edit.zones,
      schedule: edit.schedule,
      late_after: (edit.late_after || '').trim(),
      active: edit.active !== false,
      ...Object.fromEntries(POINT_ADVANCED.map(s => [s.key, edit[s.key]])),
    };
    const ok = edit.id
      ? await run('PUT', `/ui/attendance/checkpoints/${edit.id}`, body, 'this point')
      : await run('POST', '/ui/attendance/checkpoints', body, 'this point');
    if (ok) { setEdit(null); await reload(); }
  }

  async function removePoint(id) {
    if (!window.confirm('Remove this check-in point? Records already collected are kept.')) return;
    if (await run('DELETE', `/ui/attendance/checkpoints/${id}`, undefined, 'the change')) await reload();
  }

  async function saveEmployees(rows) {
    const d = await run('PUT', '/ui/attendance/employees', { employees: rows }, 'the staff list');
    if (d) await reload();
  }

  const zoneOk = (edit?.zones?.[0]?.length || 0) >= 3;

  // ── the add / edit form, shown instead of the list ──
  if (edit) {
    return (
      <>
        <div className="gs-section-title">
          {edit.id ? `Change “${edit.name || 'this point'}”` : 'New check-in point'}
        </div>

        <Field label="What is this point called?" help="Shown on the attendance report.">
          <input className="gs-input" value={edit.name} maxLength={40}
                 placeholder="e.g. Main Gate"
                 onChange={e => setEdit(p => ({ ...p, name: e.target.value }))} />
        </Field>

        <Field label="Which camera watches it?">
          <select className="gs-input" value={edit.camera_idx}
                  onChange={e => setEdit(p => ({ ...p, camera_idx: Number(e.target.value), zones: [] }))}>
            {cameras.map(c => <option key={c.idx} value={c.idx}>{c.name}</option>)}
          </select>
        </Field>

        <Field label="Is this the way in or the way out?"
               help="A camera is one or the other. Add a second point for the other direction.">
          <div className="gs-choices">
            {PURPOSES.map(p => (
              <Choice key={p.id} icon={p.icon} title={p.title} sub={p.sub}
                      selected={edit.purpose === p.id}
                      onClick={() => setEdit(x => ({ ...x, purpose: p.id }))} />
            ))}
          </div>
        </Field>

        <Field
          label="Draw where they stand"
          help="Tap the corners of the spot somebody stands in or walks through to be counted — the doorway, the turnstile, the floor in front of the gate. Without this the point is ignored completely, so it cannot be saved empty."
        >
          <PatrolZoneEditor cameraIdx={edit.camera_idx} value={edit.zones[0] || []}
                            onChange={z => setEdit(p => ({ ...p, zones: z.length ? [z] : [] }))} />
        </Field>

        <div className="gs-section-title">When is it open?</div>
        <div className="gs-input-row" style={{ maxWidth: 420 }}>
          <Field label="From">
            <input className="gs-input" type="time" value={edit.schedule.start}
                   onChange={e => setEdit(p => ({ ...p, schedule: { ...p.schedule, start: e.target.value } }))} />
          </Field>
          <Field label="To">
            <input className="gs-input" type="time" value={edit.schedule.end}
                   onChange={e => setEdit(p => ({ ...p, schedule: { ...p.schedule, end: e.target.value } }))} />
          </Field>
        </div>
        <Field label="On which days?">
          <DayPicker value={edit.schedule.active_days}
                     onChange={d => setEdit(p => ({ ...p, schedule: { ...p.schedule, active_days: d } }))} />
        </Field>

        <Field label="Count as late after" help="Leave blank and nobody is ever marked late at this point.">
          <input className="gs-input" type="time" value={edit.late_after || ''} style={{ maxWidth: 150 }}
                 onChange={e => setEdit(p => ({ ...p, late_after: e.target.value }))} />
        </Field>

        <Advanced changed={countChanged(POINT_ADVANCED, edit)}>
          {POINT_ADVANCED.map(s => (
            <SettingRow key={s.key} spec={s} value={edit[s.key]}
                        onChange={v => setEdit(p => ({ ...p, [s.key]: v }))} />
          ))}
        </Advanced>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <button className="gs-btn primary" onClick={savePoint} disabled={busy || !zoneOk}>
            {busy ? <span className="gs-spin" /> : (edit.id ? 'Save changes' : 'Add this point')}
          </button>
          <button className="gs-btn ghost" onClick={() => { setEdit(null); setErr(null); }}>Cancel</button>
          {!zoneOk && <span className="gs-foot-note">Draw the area — three corners is enough</span>}
        </div>
        {err && <div className="gs-err" role="alert">{err}</div>}
      </>
    );
  }

  // ── the list ──
  return (
    <>
      {!cfg && !err && <div className="gs-panel"><div className="gs-scanning"><span className="gs-spin" /> Loading…</div></div>}

      {cfg && (
        <>
          <div className="gs-section-title">Check-in points</div>
          <p className="gs-help">
            One per door or gate, each told which way it counts. Staff are clocked in by
            face as they walk through — there is nothing for them to press.
          </p>

          {points.length === 0 && (
            <div className="gs-empty">
              No check-in points yet. Attendance records nothing until there is one.
            </div>
          )}

          {points.map(p => {
            const ready = (p.zones || []).length > 0;
            return (
              <div className={`gs-row${ready ? '' : ' gs-row-bad'}`} key={p.id}>
                <span className="gs-row-icon">{ready ? (p.purpose === 'checkout' ? '⬅️' : '➡️') : '⚠️'}</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{p.name}</span>
                  <span className="gs-row-sub">
                    {camOf(p.camera_idx)} · {p.purpose === 'checkout' ? 'way out' : 'way in'} ·{' '}
                    {p.schedule?.start}–{p.schedule?.end}
                    {p.late_after ? ` · late after ${p.late_after}` : ''}
                    {ready ? '' : ' · NO AREA DRAWN — this point will never fire'}
                  </span>
                </span>
                <button className="gs-btn small" onClick={() => setEdit({
                  ...blankPoint(p.camera_idx), ...p,
                  schedule: { ...blankPoint(p.camera_idx).schedule, ...(p.schedule || {}) },
                })}>Change</button>
                <button className="gs-btn small ghost" onClick={() => removePoint(p.id)}>Remove</button>
              </div>
            );
          })}

          <p className="gs-help gs-help-after">
            <button className="gs-btn small primary"
                    onClick={() => setEdit(blankPoint(cameras[0]?.idx ?? 0))}>
              Add a check-in point
            </button>
          </p>

          <div className="gs-section-title">How attendance behaves</div>
          {SETTINGS.filter(s => s.ask).map(s => (
            <SettingRow key={s.key} spec={s} value={cfg[s.key]}
                        onChange={v => saveSetting(s.key, v)} />
          ))}
          <Advanced changed={countChanged(SETTINGS.filter(s => !s.ask), cfg)}>
            {SETTINGS.filter(s => !s.ask).map(s => (
              <SettingRow key={s.key} spec={s} value={cfg[s.key]}
                          onChange={v => saveSetting(s.key, v)} />
            ))}
          </Advanced>

          {/* The employee directory is what makes the report say "3 absent" rather
              than just listing who turned up — and the PACS module resolves badge
              user ids through these employee_ids, so it is worth the typing. */}
          <div className="gs-section-title">Staff list (optional)</div>
          <p className="gs-help">
            Who is <strong>expected</strong>, so the report can say who is missing rather
            than only who arrived. Leave it empty and everyone enrolled counts as staff.
            An employee number here is also how badge records are matched to a face, if
            you use badge checking.
          </p>

          {employees.map((e, i) => (
            <div className="gs-row" key={`${e.name}-${i}`}>
              <span className="gs-row-icon">👤</span>
              <span className="gs-row-text">
                <span className="gs-row-name">{e.name}</span>
                <span className="gs-row-sub">
                  {e.employee_id ? `No. ${e.employee_id}` : 'no employee number'}
                  {e.department ? ` · ${e.department}` : ''}
                </span>
              </span>
              <button className="gs-btn small ghost" disabled={busy}
                      onClick={() => saveEmployees(employees.filter((_, n) => n !== i))}>
                Remove
              </button>
            </div>
          ))}

          <EmployeeAdder staff={staff} taken={employees.map(e => e.name)} busy={busy}
                         onAdd={row => saveEmployees([...employees, row])} />

          {note && <p className="gs-help gs-help-after">{note}</p>}
          {err && <div className="gs-err" role="alert">{err}</div>}
        </>
      )}
    </>
  );
}

// Names come from the people already enrolled, rather than a free-text box: a
// directory entry whose name does not match a Known Face matches nobody, and a
// typo would be invisible until the report was wrong.
function EmployeeAdder({ staff, taken, busy, onAdd }) {
  const [name, setName] = useState('');
  const [id,   setId]   = useState('');
  const [dept, setDept] = useState('');
  const free = staff.filter(s => !taken.includes(s));

  if (!staff.length) {
    return (
      <p className="gs-help gs-help-after">
        Nobody is enrolled yet. Add your staff on the <strong>Staff</strong> step — their
        faces have to be known before they can be listed here.
      </p>
    );
  }

  return (
    <div className="gs-panel">
      <div className="gs-panel-title">Add someone to the staff list</div>
      <div className="gs-input-row" style={{ maxWidth: 640 }}>
        <Field label="Who">
          <select className="gs-input" value={name} onChange={e => setName(e.target.value)}>
            <option value="">Choose a person…</option>
            {free.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Employee number">
          <input className="gs-input" value={id} maxLength={30} placeholder="optional"
                 onChange={e => setId(e.target.value)} />
        </Field>
        <Field label="Department">
          <input className="gs-input" value={dept} maxLength={40} placeholder="optional"
                 onChange={e => setDept(e.target.value)} />
        </Field>
      </div>
      <button className="gs-btn small primary" disabled={busy || !name}
              onClick={() => { onAdd({ name, employee_id: id.trim(), department: dept.trim() });
                               setName(''); setId(''); setDept(''); }}>
        Add to the list
      </button>
      {free.length === 0 && (
        <p className="gs-help gs-help-after">Everyone enrolled is already on the list.</p>
      )}
    </div>
  );
}
